import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User } from '../types/auth';
import { getAuth, waitForFirebase } from '../services/firebase';
import { googleProvider, facebookProvider } from '../firebase';
import {
  onAuthStateChanged,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  User as FirebaseAuthUser
} from 'firebase/auth';
import { toast } from 'react-hot-toast';
import * as authService from '../services/authService';
import UserActivityService from '../services/userActivityService';

// 添加調試日誌
// 调试日志已移除

const USER_STORAGE_KEY = 'user';

// 🔧 修復：更新 Firestore 中用戶的最後登入時間並確保創建完整記錄
const updateFirestoreUserLastLogin = async (userId: string, userName: string, userEmail: string) => {
  try {
    const { getDb, waitForFirebase } = await import('../services/firebase');
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.warn('⚠️ Firestore 未初始化，跳過更新最後登入時間');
      return;
    }

    const { doc, setDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
    const userRef = doc(db, 'users', userId);
    
    // 先檢查用戶記錄是否存在
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // 用戶記錄存在，只更新最後登入時間
      await setDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isOnline: true
      }, { merge: true });
      console.log('✅ Firestore 用戶最後登入時間已更新:', userId);
    } else {
      // 用戶記錄不存在，創建完整的用戶記錄
      await setDoc(userRef, {
        userId,
        name: userName || userEmail?.split('@')[0] || '未設定',
        displayName: userName || userEmail?.split('@')[0] || '未設定',
        email: userEmail,
        lastLoginAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isOnline: true
      });
      console.log('✅ Firestore 用戶記錄已創建:', userId);
    }
  } catch (error) {
    console.warn('⚠️ 更新 Firestore 用戶記錄失敗:', error);
  }
};

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  handleGoogleLogin: () => Promise<void>;
  handleFacebookLogin: () => Promise<void>;
  handleEmailLogin: (email: string, password: string) => Promise<void>;
  handleRegister: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('❌ useAuth must be used within an AuthProvider');
    // 🔧 修復：提供默認值避免崩潰
    return {
      user: null,
      isAuthenticated: false,
      loading: false,
      isAdmin: false,
      error: null,
      setUser: () => {},
      setLoading: () => {},
      setIsAuthenticated: () => {},
      handleEmailLogin: () => Promise.reject(new Error('Auth not available')),
      handleRegister: () => Promise.reject(new Error('Auth not available')),
      handleGoogleLogin: () => Promise.reject(new Error('Auth not available')),
      handleFacebookLogin: () => Promise.reject(new Error('Auth not available')),
      logout: () => Promise.resolve()
    } as AuthContextType;
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 🔧 方案 A：從 localStorage 預載入用戶狀態，避免白屏等待
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (stored) {
        const userData = JSON.parse(stored);
        // 调试日志已移除
        return userData;
      }
    } catch (error) {
      console.warn('⚠️ localStorage 讀取失敗:', error);
    }
    return null;
  });
  
  // 🔧 方案 A：初始 loading 設為 false，允許立即渲染
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!user);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastAuthState, setLastAuthState] = useState<string | null>(null);
  const processedUsersRef = useRef<Set<string>>(new Set());

  const processPostLoginSideEffects = async (firebaseUser: FirebaseAuthUser) => {
    if (processedUsersRef.current.has(firebaseUser.uid)) {
      return;
    }
    processedUsersRef.current.add(firebaseUser.uid);

    try {
      const { createUser } = await import('../services/userService');
      await createUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        isOnline: true
      });
      console.log('✅ 用戶記錄已創建/更新到 users 集合');
    } catch (userError) {
      console.warn('⚠️ 創建用戶記錄失敗，但登入繼續:', userError);
    }

    try {
      const [watchHistoryResult, groupsResult] = await Promise.allSettled([
        fetch(`/api/user-watch-history/${firebaseUser.uid}`).then(res => res.json()),
        fetch(`/api/user-groups/${firebaseUser.uid}`).then(res => res.json())
      ]);

      if (watchHistoryResult.status === 'fulfilled') {
        console.log('✅ 預載入觀看記錄完成:', watchHistoryResult.value.records?.length || 0, '個記錄');
        localStorage.setItem('userWatchHistory', JSON.stringify(watchHistoryResult.value.records || []));
      }

      if (groupsResult.status === 'fulfilled') {
        console.log('✅ 預載入用戶群組完成:', groupsResult.value.groups?.length || 0, '個群組');
        localStorage.setItem('userGroups', JSON.stringify(groupsResult.value.groups || []));
      }
    } catch (error) {
      console.warn('⚠️ 預載入 NotesPage 數據失敗，但不影響登入:', error);
    }
  };

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        console.log('檢查重定向登入結果...');
        console.log('[AuthDebug] sessionStorage before getRedirectResult:', {
          pendingRedirect: sessionStorage.getItem('pendingRedirect'),
          redirectTimestamp: sessionStorage.getItem('redirectTimestamp'),
          returnUrl: sessionStorage.getItem('returnUrl')
        });
        
        // 檢查是否有重定向結果
        // 调试日志已移除
        // 调试日志已移除
        // 调试日志已移除
        
        const auth = getAuth();
        if (!auth) {
          console.warn('⚠️ Firebase Auth 尚未初始化，跳過重定向檢查');
          return;
        }
        
        // ✅ 修復：添加延遲和重試，確保 Firebase 完成處理
        let result = await getRedirectResult(auth);
        
        // 如果第一次沒有結果，等待並重試
        if (!result) {
          console.log('第一次檢查無結果，等待 Firebase 處理...');
          await new Promise(resolve => setTimeout(resolve, 500));
          result = await getRedirectResult(auth);
        }
        
        console.log('🔍 重定向結果:', result ? '有結果' : '無結果');
        console.log('[AuthDebug] sessionStorage after getRedirectResult:', {
          pendingRedirect: sessionStorage.getItem('pendingRedirect'),
          redirectTimestamp: sessionStorage.getItem('redirectTimestamp'),
          returnUrl: sessionStorage.getItem('returnUrl')
        });
        
        if (result && result.user) {
          console.log('重定向登入成功:', {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
            providerData: result.user.providerData?.map(item => item?.providerId)
          });
          
          // 更新用戶狀態
          const userProfile = {
            uid: result.user.uid,
            email: result.user.email,
            name: result.user.displayName,
            photoURL: result.user.photoURL
          };
          console.log('[AuthDebug] setUser from redirect result:', userProfile);
          setUser(userProfile);
          setIsAuthenticated(true);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userProfile));
          
          // 清除所有重定向相關的狀態
          sessionStorage.removeItem('authState');
          sessionStorage.removeItem('pendingRedirect');
          sessionStorage.removeItem('pendingFacebookLogin');
          
          // 🚀 優化：在背景處理後續資料，不阻塞登入導向流程
          processPostLoginSideEffects(result.user).catch(error => {
            console.warn('⚠️ 背景處理登入後資料失敗，但不影響登入:', error);
          });

          // 如果有保存的返回 URL，則重定向回去
          const returnUrl = sessionStorage.getItem('returnUrl');
          if (returnUrl) {
            console.log('重定向回原始頁面:', returnUrl);
            sessionStorage.removeItem('returnUrl');
            if (returnUrl === window.location.pathname) {
              window.location.reload();
            } else {
              window.location.href = returnUrl;
            }
          } else {
            if (window.location.pathname === '/app') {
              window.location.href = '/open';
            } else {
              window.location.reload();
            }
          }
        } else {
          console.log('沒有重定向登入結果', {
            hasResultObject: !!result,
            hasUser: !!result?.user
          });
          
          // 檢查是否有過期的重定向狀態
          const pendingRedirect = sessionStorage.getItem('pendingRedirect');
          console.log('[AuthDebug] pendingRedirect flag:', pendingRedirect);
          if (pendingRedirect) {
            const timestamp = parseInt(sessionStorage.getItem('redirectTimestamp') || '0', 10);
            const now = Date.now();
            // 如果狀態超過 5 分鐘，則清除
            if (now - timestamp > 5 * 60 * 1000) {
              console.log('清除過期的重定向狀態');
              sessionStorage.removeItem('pendingRedirect');
              sessionStorage.removeItem('redirectTimestamp');
              sessionStorage.removeItem('pendingFacebookLogin');
              sessionStorage.removeItem('returnUrl');
            }
          }
        }
      } catch (error) {
        console.error('處理重定向結果時出錯:', error);
        setError('登入處理失敗，請重試');
        // 清除重定向狀態
        sessionStorage.removeItem('pendingRedirect');
        sessionStorage.removeItem('redirectTimestamp');
        sessionStorage.removeItem('pendingFacebookLogin');
      }
    };

    // 🔧 修復：總是檢查重定向結果，因為用戶可能剛從 Google 認證返回
    // 调试日志已移除
    checkRedirectResult();
  }, []);

  // 監聽認證狀態變化 - 優化版本，避免重複監聽器
  useEffect(() => {
    console.log('設置 Firebase 認證狀態監聽器...');
    
    // 🔍 觀察目前的 Firebase Auth 狀態（但不再強制 signOut，避免造成第二次登入循環）
    try {
      const auth = getAuth();
      if (auth) {
        console.log('[AuthDebug] 初始化時的 auth.currentUser:', auth.currentUser ? {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email
        } : 'null', 'pendingRedirect:', sessionStorage.getItem('pendingRedirect'));
      }
    } catch (error) {
      console.warn('⚠️ Firebase Auth 狀態檢查失敗:', error);
    }
    
    // 檢查是否已經有監聽器在運行
    let isUnsubscribed = false;
    let unsubscribe: (() => void) | null = null;
    
    // 等待 Firebase 初始化完成
    waitForFirebase().then(() => {
      const auth = getAuth();
      if (!auth) {
        console.warn('⚠️ Firebase Auth 初始化後仍為 null');
        return;
      }
      
      console.log('✅ Firebase Auth 已準備就緒，設置 onAuthStateChanged');
      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        // 如果組件已卸載，停止處理
        if (isUnsubscribed) return;
        
        const currentAuthState = firebaseUser ? firebaseUser.uid : 'null';
        
        // 如果認證狀態沒有變化，直接返回
        if (lastAuthState === currentAuthState) {
          console.log('認證狀態未變化，跳過更新:', currentAuthState);
          return;
        }
        
        console.log('認證狀態變更:', {
          nextState: firebaseUser ? '已登入' : '未登入',
          previousState: lastAuthState,
          currentState: currentAuthState,
          pendingRedirect: sessionStorage.getItem('pendingRedirect'),
          redirectTimestamp: sessionStorage.getItem('redirectTimestamp'),
          location: window.location.pathname + window.location.search
        });
        
        // 立即更新狀態，不使用防抖機制
        if (firebaseUser) {
          const userProfile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL
          };
          console.log('用戶信息:', userProfile);
          console.log('[AuthDebug] setUser from onAuthStateChanged');
          setUser(userProfile);
          setIsAuthenticated(true);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userProfile));
          
          // 🔧 修復：更新 Firestore 中的用戶最後登入時間
          updateFirestoreUserLastLogin(firebaseUser.uid, firebaseUser.displayName || 'Unknown', firebaseUser.email || '');
          
          processPostLoginSideEffects(firebaseUser).catch(error => {
            console.warn('⚠️ 後續登入處理失敗:', error);
          });

          // 開始追蹤用戶活動
          // 调试日志已移除
          const activityService = UserActivityService.getInstance();
          // 调试日志已移除
          activityService.startTracking(
            firebaseUser.uid,
            firebaseUser.displayName || 'Unknown',
            firebaseUser.email || ''
          );
        } else {
          console.log('用戶未登入或已登出');
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem(USER_STORAGE_KEY);
          
          // 停止追蹤用戶活動
          const activityService = UserActivityService.getInstance();
          activityService.stopTracking();
        }
        
        // 🔧 修復：確保 loading 狀態被正確設置
        // 调试日志已移除
        setLoading(false);
        setLastAuthState(currentAuthState);
      });
    }).catch((error) => {
      console.error('❌ Firebase 初始化等待失敗:', error);
      setLoading(false);
    });

    return () => {
      isUnsubscribed = true;
      if (unsubscribe) unsubscribe();
    };
  }, []); // 移除 lastAuthState 依賴，避免重複監聽器

  const handleGoogleLogin = async () => {
    // 🔧 修復：防止重複調用
    if (loading) {
      console.log('🔐 登入操作進行中，跳過重複調用');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // 🚀 優化：預載入登入後的數據
    // 🔧 改善：添加超時和錯誤處理，確保不會拖慢登入速度
    const preloadData = async () => {
      try {
        // 调试日志已移除
        
        // 🔧 優化：設置超時時間，避免長時間等待
        const fetchWithTimeout = async (url: string, timeout = 3000) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);
          
          try {
            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);
            return response;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        };
        
        // 🔧 優化：並行預載入，使用 Promise.allSettled 確保單個失敗不影響其他
        await Promise.allSettled([
          // 預載入社群影片數據
          fetchWithTimeout('/api/community-public-videos')
            .then(async response => {
              if (!response.ok) return null;
              // 🔧 修復：檢查 Content-Type，避免 HTML 被當作 JSON 解析
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                return response.json();
              }
              return null;
            })
            .then(data => {
              if (data?.videos) {
                console.log('✅ 預載入社群影片完成:', data.videos.length, '個影片');
              }
            })
            .catch(error => {
              // 靜默處理，不輸出詳細錯誤
              if (error.name !== 'AbortError') {
                console.warn('⚠️ 預載入社群影片跳過（頁面切換或網絡問題）');
              }
            }),
          
          // 預載入專家標籤
          fetchWithTimeout('/api/expert-tags')
            .then(async response => {
              if (!response.ok) return null;
              // 🔧 修復：檢查 Content-Type，避免 HTML 被當作 JSON 解析
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                return response.json();
              }
              return null;
            })
            .then(data => {
              if (data?.tags) {
                console.log('✅ 預載入專家標籤完成:', data.tags.length, '個標籤');
              }
            })
            .catch(error => {
              // 靜默處理，不輸出詳細錯誤
              if (error.name !== 'AbortError') {
                console.warn('⚠️ 預載入專家標籤跳過（頁面切換或網絡問題）');
              }
            })
        ]);
        
        console.log('✅ 預載入完成（成功或失敗都不影響登入）');
      } catch (error) {
        console.warn('⚠️ 預載入數據失敗，但不影響登入:', error);
      }
    };
    
    // 並行執行登入和預載入
    try {
      console.log('🔐 開始 Google 登入流程');
      // 🔧 修復：等待 Firebase 初始化完成
      await waitForFirebase();
      
      // 🚀 優化：並行執行登入和預載入，但不等待預載入完成
      preloadData(); // 不使用 await，讓預載入在背景執行
      
      const auth = getAuth();
      if (!auth) {
        setError('Firebase Auth 尚未初始化，請稍後再試');
        setLoading(false);
        return;
      }

      await setPersistence(auth, browserLocalPersistence);

      try {
        console.log('🔐 使用 popup 模式啟動 Google 登入');
        await signInWithPopup(auth, googleProvider);
      } catch (popupError: any) {
        console.error('Google popup 登入失敗:', popupError);
        let message = popupError?.message || 'Google 登入失敗';

        if (popupError?.code === 'auth/popup-blocked') {
          message = '瀏覽器阻擋了登入視窗，請允許彈出視窗後再試一次。';
        } else if (popupError?.code === 'auth/popup-closed-by-user') {
          message = '您已關閉登入視窗，請重新嘗試。';
        } else if (popupError?.code === 'auth/cancelled-popup-request') {
          message = '登入視窗已被取消，請重新嘗試。';
        } else if (popupError?.code === 'auth/operation-not-supported-in-this-environment') {
          message = '此瀏覽器不支援彈出式登入，請改用桌面版或允許登入視窗。';
        }

        setError(message);
        setLoading(false);
        return;
      }
    } catch (error: any) {
      console.error('Google 登入失敗:', error);
      setError(error.message || 'Google 登入失敗');
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const handleFacebookLogin = async () => {
    try {
      // 🔧 修復：等待 Firebase 初始化完成
      await waitForFirebase();
      
      const auth = getAuth();
      if (!auth) {
        setError('Firebase Auth 尚未初始化，請稍後再試');
        return;
      }
      await signInWithRedirect(auth, facebookProvider);
    } catch (error: any) {
      console.error('Facebook 登入失敗:', error);
      setError(error.message || 'Facebook 登入失敗');
    }
  };

  const handleEmailLogin = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await authService.loginWithEmail(email, password);
    } catch (error: any) {
      setError(error.message || 'Email login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (email: string, password: string, name: string) => {
    console.log('handleRegister called', email, password, name);
    setLoading(true);
    setError(null);
    try {
      await authService.registerWithEmail(email, password, name);
    } catch (error: any) {
      setError(error.message || 'Register failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
      setLoading(true);
    setError(null);
    try {
      // 停止追蹤用戶活動
      const activityService = UserActivityService.getInstance();
      activityService.stopTracking();
      
      // 先清除本地狀態
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem(USER_STORAGE_KEY);
      
      // 清除所有相關的 sessionStorage
      sessionStorage.removeItem('authState');
      sessionStorage.removeItem('pendingRedirect');
      sessionStorage.removeItem('pendingFacebookLogin');
      sessionStorage.removeItem('returnUrl');
      sessionStorage.removeItem('redirectTimestamp');
      
      // 執行 Firebase 登出
      await authService.logout();
      
    } catch (error: any) {
      setError(error.message || 'Logout failed');
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    error,
    setUser,
    setLoading,
    setIsAuthenticated,
    handleGoogleLogin,
    handleFacebookLogin,
    handleEmailLogin,
    handleRegister,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 